import { z } from 'zod';

// ────────────────────────────────────────────────────────────────────────────
// Pagination helpers (inlined — no external common schema needed)
// ────────────────────────────────────────────────────────────────────────────

const PaginationSchema = z.object({
  lastKey: z.string().optional().nullable(),
  limit: z.number().int().min(1).max(1000).default(200),
  nextToken: z.string().optional().nullable(),
});

const PaginatedResponseSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    count: z.number().int().min(0),
    items: z.array(itemSchema),
    lastKey: z.string().optional().nullable(),
    nextToken: z.string().optional().nullable(),
  });

// ────────────────────────────────────────────────────────────────────────────
// Order Release
// ────────────────────────────────────────────────────────────────────────────

export const OrderReleaseStatusSchema = z.enum([
  'Open',
  'Pending',
  'At-Customer',
  'Cancelled',
  'Complete',
  'In transit',
  'Scheduled',
]);

export type IOrderReleaseStatus = z.infer<typeof OrderReleaseStatusSchema>;

export const OrderReleaseSchema = z.object({
  bolNumber: z.string().nullable(),
  carrier: z.string().nullable(),
  carrierId: z.string().nullable().catch(null),
  contract: z.string().nullable(),
  crossover: z.string().nullable(),
  deliveredDate: z.string().nullable(),
  deliverToAddressId: z.string().nullable().catch(null),
  destination: z.string().nullable(),
  driverAssigned: z.string().nullable(),
  driverNotes: z.string().nullable().catch(null),
  eta: z.string().nullable(),
  id: z.string(),
  invoiceNumber: z.string().nullable(),
  isBrokered: z.boolean(),
  isUrgent: z.boolean(),
  origin: z.string().nullable(),
  poNumber: z.string().nullable(),
  product: z.string(),
  railcarNumber: z.string().nullable(),
  scheduledPickupDateTime: z.string().nullable(),
  scheduledQuantity: z.number().nullable(),
  shipDate: z.string().nullable(),
  shipFromCity: z.string().nullable(),
  shipmentWindowEnd: z.string().nullable(),
  shipmentWindowStart: z.string().nullable(),
  shippedDate: z.string().nullable(),
  shippedQuantity: z.number().nullable(),
  shipQuantity: z.number().nullable(),
  status: OrderReleaseStatusSchema,
  trailerAssigned: z.string().nullable(),
  transportMode: z.string().nullable(),
  uom: z.string().nullable(),
});

export type IOrderRelease = z.infer<typeof OrderReleaseSchema>;

// List query schema
export const OrderReleaseListQuerySchema = PaginationSchema.extend({
  bolNumber: z.string().optional(),
  carrier: z.string().optional(),
  contract: z.string().optional(),
  destination: z.string().optional(),
  origin: z.string().optional(),
  product: z.string().optional(),
  status: z.array(OrderReleaseStatusSchema).optional(),
});

export type IOrderReleaseListQuery = z.infer<
  typeof OrderReleaseListQuerySchema
>;

export const OrderReleaseListResponseSchema =
  PaginatedResponseSchema(OrderReleaseSchema);

export type IOrderReleaseListResponse = z.infer<
  typeof OrderReleaseListResponseSchema
>;
